import { createReducer, on } from '@ngrx/store';
import { CompaniesState } from '../../models/stock.model';
import * as StocksActions from './stocks.actions';

export const initialState: CompaniesState = {
  companies: [],
  selectedStock: null,
  sectors: [],
  markets: [],
  industries: [],
  templates: [],
  templateProperties: [],
  settings: null, // Add settings property
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
  },
};

export const stocksReducer = createReducer(
  initialState,

  // Load Stocks (plural - for list)
  on(StocksActions.loadStocks, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(StocksActions.loadStocksSuccess, (state, { stocks, pagination }) => ({
    ...state,
    companies: stocks,
    pagination: pagination,
    loading: false,
  })),

  on(StocksActions.loadStocksFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Load Single Stock (singular - for detail)
  on(StocksActions.loadStock, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(StocksActions.loadStockSuccess, (state, { stock }) => ({
    ...state,
    selectedStock: stock,
    loading: false,
  })),
  on(StocksActions.loadStockFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Create Stock
  on(StocksActions.createStock, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(StocksActions.createStockSuccess, (state, { stock }) => ({
    ...state,
    companies: [...state.companies, stock],
    loading: false,
  })),
  on(StocksActions.createStockFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Update Stock
  on(StocksActions.updateStock, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(StocksActions.updateStockSuccess, (state, { stock }) => ({
    ...state,
    companies: state.companies.map((c) => (c.id === stock.id ? stock : c)),
    selectedStock: stock,
    loading: false,
  })),
  on(StocksActions.updateStockFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Delete Stock
  on(StocksActions.deleteStock, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(StocksActions.deleteStockSuccess, (state, { id }) => ({
    ...state,
    companies: state.companies.filter((c) => c.id !== id),
    loading: false,
  })),
  on(StocksActions.deleteStockFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Activate Stock
  on(StocksActions.activateStock, (state) => ({ ...state, loading: true, error: null })),
  on(StocksActions.activateStockSuccess, (state, { id }) => ({
    ...state,
    companies: state.companies.map((c) => (c.id === id ? { ...c, isActive: true } : c)),
    loading: false,
  })),
  on(StocksActions.activateStockFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Deactivate Stock
  on(StocksActions.deactivateStock, (state) => ({ ...state, loading: true, error: null })),
  on(StocksActions.deactivateStockSuccess, (state, { id }) => ({
    ...state,
    companies: state.companies.map((c) => (c.id === id ? { ...c, isActive: false } : c)),
    loading: false,
  })),
  on(StocksActions.deactivateStockFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Load Sectors
  on(StocksActions.loadSectors, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(StocksActions.loadSectorsSuccess, (state, { sectors }) => ({
    ...state,
    sectors,
    loading: false,
  })),
  on(StocksActions.loadSectorsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Load Markets
  on(StocksActions.loadMarkets, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(StocksActions.loadMarketsSuccess, (state, { markets }) => ({
    ...state,
    markets,
    loading: false,
  })),
  on(StocksActions.loadMarketsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Load Industries
  on(StocksActions.loadIndustries, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(StocksActions.loadIndustriesSuccess, (state, { industries }) => ({
    ...state,
    industries,
    loading: false,
  })),
  on(StocksActions.loadIndustriesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Load Templates
  on(StocksActions.loadTemplates, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(StocksActions.loadTemplatesSuccess, (state, { templates }) => ({
    ...state,
    templates: templates,
    loading: false,
    error: null,
  })),

  on(StocksActions.loadTemplatesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Load Template Properties
  on(StocksActions.loadTemplateProperties, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(StocksActions.loadTemplatePropertiesSuccess, (state, { properties }) => ({
    ...state,
    templateProperties: properties,
    loading: false,
  })),
  on(StocksActions.loadTemplatePropertiesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  // Load Settings
  on(StocksActions.loadSettings, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(StocksActions.loadSettingsSuccess, (state, { settings }) => ({
    ...state,
    settings,
    loading: false,
    error: null,
  })),
  on(StocksActions.loadSettingsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  }))
);

export function reducer(state: CompaniesState | undefined, action: any) {
  return stocksReducer(state, action);
}
